import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useSettings } from '@/context/SettingsContext';
import { applyBranding, lighten } from '@/lib/theme';
import { COLOR_OPTIONS } from '@/lib/icons';
import { HeroLogo } from '@/components/HeroLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Upload, Trash2, Check, RotateCcw, Palette, Image as ImageIcon } from 'lucide-react';

const MAX_LOGO = 1_800_000; // ~1.3MB data URL

export function BrandingPanel() {
  const { settings, setSettings } = useSettings();
  const savedRef = useRef(settings);

  const [brandName, setBrandName] = useState('');
  const [domain, setDomain] = useState('');
  const [c1, setC1] = useState('#2EE6C5');
  const [c2, setC2] = useState('#2BB6FF');
  const [logo, setLogo] = useState('');
  const [logoWordmark, setLogoWordmark] = useState('');
  const [heroStyle, setHeroStyle] = useState('panel');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      savedRef.current = settings;
      setBrandName(settings.brand_name || 'Commissioning Tools');
      setDomain(settings.domain || 'cxatools.com');
      setC1(settings.accent_color || '#2EE6C5');
      setC2(settings.accent_color_2 || '#2BB6FF');
      setLogo(settings.logo || '');
      setLogoWordmark(settings.logo_wordmark || '');
      setHeroStyle(settings.hero_logo_style === 'plain' ? 'plain' : 'panel');
    }
  }, [settings]);

  // Live preview of accent while editing
  useEffect(() => {
    applyBranding({ accent_color: c1, accent_color_2: c2 });
  }, [c1, c2]);

  // Revert to saved theme if user leaves without saving
  useEffect(() => () => { applyBranding(savedRef.current); }, []);

  const pickPreset = (opt) => { setC1(opt.from); setC2(opt.to); };

  const onCustom = (val) => {
    setC1(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) setC2(lighten(val, 12));
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl === 'string' && dataUrl.length > MAX_LOGO) {
        toast.error('That image is too large. Please use one under ~1.3MB.');
        return;
      }
      setLogo(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const onWordmarkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl === 'string' && dataUrl.length > MAX_LOGO) {
        toast.error('That image is too large. Please use one under ~1.3MB.');
        return;
      }
      setLogoWordmark(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings', {
        brand_name: brandName, domain, accent_color: c1, accent_color_2: c2, logo,
        logo_wordmark: logoWordmark, hero_logo_style: heroStyle,
      });
      savedRef.current = res.data;
      setSettings(res.data);
      toast.success('Branding saved', { description: 'Your portal look has been updated.' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save branding.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    const s = savedRef.current || {};
    setBrandName(s.brand_name || 'Commissioning Tools');
    setDomain(s.domain || 'cxatools.com');
    setC1(s.accent_color || '#2EE6C5');
    setC2(s.accent_color_2 || '#2BB6FF');
    setLogo(s.logo || '');
    setLogoWordmark(s.logo_wordmark || '');
    setHeroStyle(s.hero_logo_style === 'plain' ? 'plain' : 'panel');
    applyBranding(s);
  };

  return (
    <div className="space-y-5">
      {/* Preview */}
      <div className="cx-glass rounded-2xl p-5">
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="logo preview" className="h-12 w-12 rounded-xl object-cover border border-[color:var(--cx-border)]" data-testid="branding-logo-preview" />
          ) : (
            <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
              <ImageIcon className="h-5 w-5 text-[#04120f]" />
            </div>
          )}
          <div>
            <div className="font-display text-lg text-[color:var(--cx-fg)]">{brandName || 'Commissioning Tools'}</div>
            <div className="font-mono text-xs text-[color:var(--cx-muted)]">{domain || 'cxatools.com'}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})`, color: '#04120f' }} className="pointer-events-none">Primary</Button>
          </div>
        </div>
      </div>

      {/* Identity */}
      <div className="cx-glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[color:var(--cx-border)] flex items-center gap-2">
          <Palette className="h-4 w-4 text-[color:var(--cx-primary)]" />
          <h2 className="font-display text-[color:var(--cx-fg)]">Brand identity</h2>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[color:var(--cx-fg-muted)]">Portal name</Label>
              <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Commissioning Tools" data-testid="branding-brand-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[color:var(--cx-fg-muted)]">Domain label</Label>
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="cxatools.com" className="font-mono" data-testid="branding-domain" />
            </div>
          </div>

          {/* Logo mark (compact) */}
          <div className="space-y-2">
            <Label className="text-[color:var(--cx-fg-muted)]">Logo mark (top bar & login)</Label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--cx-border)] px-3 py-2 text-sm text-[color:var(--cx-fg-muted)] hover:border-[rgba(46,230,197,0.4)] hover:text-[color:var(--cx-fg)] cursor-pointer transition-colors">
                <Upload className="h-4 w-4" />Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="branding-logo-input" />
              </label>
              {logo && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setLogo('')} data-testid="branding-logo-remove" className="text-[color:var(--destructive)]">
                  <Trash2 className="h-4 w-4 mr-1.5" />Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-[color:var(--cx-muted)]">A small square emblem/icon. Shown in the top bar and on the login screen. Max ~1.3MB.</p>
          </div>
        </div>
      </div>

      {/* Hero wordmark */}
      <div className="cx-glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[color:var(--cx-border)] flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-[color:var(--cx-primary)]" />
          <div>
            <h2 className="font-display text-[color:var(--cx-fg)]">Hero logo</h2>
            <p className="text-sm text-[color:var(--cx-fg-muted)] mt-0.5">The full wordmark shown large at the top of the dashboard.</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {/* Live preview */}
          <div className="rounded-xl border border-[color:var(--cx-border)] overflow-hidden">
            <div className="relative p-6 bg-[color:var(--cx-bg)]" style={{ backgroundImage: 'var(--cx-hero-overlay)' }}>
              <HeroLogo settings={{ logo_wordmark: logoWordmark, hero_logo_style: heroStyle, brand_name: brandName }} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--cx-border)] px-3 py-2 text-sm text-[color:var(--cx-fg-muted)] hover:border-[rgba(46,230,197,0.4)] hover:text-[color:var(--cx-fg)] cursor-pointer transition-colors">
              <Upload className="h-4 w-4" />Upload wordmark
              <input type="file" accept="image/*" className="hidden" onChange={onWordmarkFile} data-testid="branding-wordmark-input" />
            </label>
            {logoWordmark && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setLogoWordmark('')} data-testid="branding-wordmark-remove" className="text-[color:var(--destructive)]">
                <Trash2 className="h-4 w-4 mr-1.5" />Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-[color:var(--cx-muted)]">A wide, full-color logo (PNG/SVG/WebP with transparent background works best). Max ~1.3MB.</p>

          {/* Style toggle */}
          <div className="space-y-2">
            <Label className="text-[color:var(--cx-fg-muted)]">Display style</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="branding-hero-style-toggle">
              <button type="button" onClick={() => setHeroStyle('glow')} data-testid="branding-hero-style-glow"
                className={`rounded-xl border p-3 text-left transition-colors ${heroStyle === 'glow' ? 'border-[color:var(--cx-primary)] bg-[rgba(46,230,197,0.08)]' : 'border-[color:var(--cx-border)] hover:border-[rgba(46,230,197,0.4)]'}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${heroStyle === 'glow' ? 'border-[color:var(--cx-primary)]' : 'border-[color:var(--cx-muted)]'}`}>
                    {heroStyle === 'glow' && <span className="h-2 w-2 rounded-full bg-[color:var(--cx-primary)]" />}
                  </span>
                  <span className="text-sm text-[color:var(--cx-fg)]">On dark (glow)</span>
                </div>
                <p className="mt-1 text-xs text-[color:var(--cx-muted)]">Original colors on the dark hero with a teal glow behind.</p>
              </button>
              <button type="button" onClick={() => setHeroStyle('panel')} data-testid="branding-hero-style-panel"
                className={`rounded-xl border p-3 text-left transition-colors ${heroStyle === 'panel' ? 'border-[color:var(--cx-primary)] bg-[rgba(46,230,197,0.08)]' : 'border-[color:var(--cx-border)] hover:border-[rgba(46,230,197,0.4)]'}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${heroStyle === 'panel' ? 'border-[color:var(--cx-primary)]' : 'border-[color:var(--cx-muted)]'}`}>
                    {heroStyle === 'panel' && <span className="h-2 w-2 rounded-full bg-[color:var(--cx-primary)]" />}
                  </span>
                  <span className="text-sm text-[color:var(--cx-fg)]">White plate</span>
                </div>
                <p className="mt-1 text-xs text-[color:var(--cx-muted)]">Full color on a clean white card.</p>
              </button>
              <button type="button" onClick={() => setHeroStyle('plain')} data-testid="branding-hero-style-plain"
                className={`rounded-xl border p-3 text-left transition-colors ${heroStyle === 'plain' ? 'border-[color:var(--cx-primary)] bg-[rgba(46,230,197,0.08)]' : 'border-[color:var(--cx-border)] hover:border-[rgba(46,230,197,0.4)]'}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${heroStyle === 'plain' ? 'border-[color:var(--cx-primary)]' : 'border-[color:var(--cx-muted)]'}`}>
                    {heroStyle === 'plain' && <span className="h-2 w-2 rounded-full bg-[color:var(--cx-primary)]" />}
                  </span>
                  <span className="text-sm text-[color:var(--cx-fg)]">Minimal white</span>
                </div>
                <p className="mt-1 text-xs text-[color:var(--cx-muted)]">Recolored to solid white on the dark hero.</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Accent */}
      <div className="cx-glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[color:var(--cx-border)]">
          <h2 className="font-display text-[color:var(--cx-fg)]">Accent color</h2>
          <p className="text-sm text-[color:var(--cx-fg-muted)] mt-0.5">Applied across buttons, highlights and the logo mark.</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="branding-accent-swatches">
            {COLOR_OPTIONS.map((opt) => {
              const active = c1.toLowerCase() === opt.from.toLowerCase();
              return (
                <button type="button" key={opt.key} onClick={() => pickPreset(opt)} title={opt.key} data-testid="branding-accent-swatch"
                  className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-transform ${active ? 'scale-110' : 'hover:scale-105'}`}
                  style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})`, borderColor: active ? 'var(--cx-fg)' : 'transparent' }}>
                  {active && <Check className="h-4 w-4 text-[#04120f]" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <input type="color" value={c1} onChange={(e) => onCustom(e.target.value)} className="h-9 w-12 rounded cursor-pointer bg-transparent border border-[color:var(--cx-border)]" data-testid="branding-accent-color-input" />
            <Input value={c1} onChange={(e) => onCustom(e.target.value)} placeholder="#2EE6C5" className="font-mono w-40" data-testid="branding-accent-custom" />
            <span className="text-xs text-[color:var(--cx-muted)]">Custom hex</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={reset}><RotateCcw className="h-4 w-4 mr-1.5" />Reset</Button>
        <Button onClick={save} disabled={saving} data-testid="branding-save-button">
          {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : (<><Check className="h-4 w-4 mr-2" />Save branding</>)}
        </Button>
      </div>
    </div>
  );
}
